import { writable } from 'svelte/store';
import { disableAnalytics, enableAnalytics } from '$lib/analytics';

const STORAGE_KEY: string = 'analytics_consent';

export type AnalyticsConsentStatus =
	| 'checking'
	| 'choice-required'
	| 'automatic'
	| 'granted'
	| 'denied';

export type AnalyticsConsentState = {
	status: AnalyticsConsentStatus;
	choicesOpen: boolean;
};

type StoredConsent = 'granted' | 'denied';
type PrivacyRegionResponse = {
	consentRequired: boolean;
};

const initialState: AnalyticsConsentState = {
	status: 'checking',
	choicesOpen: false
};

export const analyticsConsent = writable<AnalyticsConsentState>(initialState);

let initialization: Promise<void> | undefined;

function getStoredConsent(): StoredConsent | undefined {
	const value: string | null = localStorage.getItem(STORAGE_KEY);
	return value === 'granted' || value === 'denied' ? value : undefined;
}

async function fetchConsentRequirement(): Promise<boolean> {
	const response: Response = await fetch('/api/privacy-region', {
		headers: {
			Accept: 'application/json'
		}
	});
	if (!response.ok) {
		throw new Error(`Privacy region request failed with status ${response.status}`);
	}

	const body: unknown = await response.json();
	if (
		typeof body !== 'object' ||
		body === null ||
		typeof (body as Partial<PrivacyRegionResponse>).consentRequired !== 'boolean'
	) {
		throw new Error('Privacy region response was invalid');
	}
	return (body as PrivacyRegionResponse).consentRequired;
}

async function initialize(): Promise<void> {
	const storedConsent: StoredConsent | undefined = getStoredConsent();
	if (storedConsent === 'granted') {
		enableAnalytics();
		analyticsConsent.set({ status: 'granted', choicesOpen: false });
		return;
	}
	if (storedConsent === 'denied') {
		disableAnalytics();
		analyticsConsent.set({ status: 'denied', choicesOpen: false });
		return;
	}

	try {
		const consentRequired: boolean = await fetchConsentRequirement();
		if (consentRequired) {
			disableAnalytics();
			analyticsConsent.set({ status: 'choice-required', choicesOpen: true });
		} else {
			enableAnalytics();
			analyticsConsent.set({ status: 'automatic', choicesOpen: false });
		}
	} catch (error) {
		console.error('Could not determine whether analytics consent is required.', error);
		disableAnalytics();
		analyticsConsent.set({ status: 'choice-required', choicesOpen: true });
	}
}

export function initializeAnalyticsConsent(): Promise<void> {
	initialization ??= initialize();
	return initialization;
}

export function allowAnalytics(): void {
	localStorage.setItem(STORAGE_KEY, 'granted');
	enableAnalytics();
	analyticsConsent.set({ status: 'granted', choicesOpen: false });
}

export function declineAnalytics(): void {
	localStorage.setItem(STORAGE_KEY, 'denied');
	disableAnalytics();
	analyticsConsent.set({ status: 'denied', choicesOpen: false });
}

export function openAnalyticsChoices(): void {
	analyticsConsent.update((state: AnalyticsConsentState) => ({
		...state,
		choicesOpen: true
	}));
}

export function closeAnalyticsChoices(): void {
	analyticsConsent.update((state: AnalyticsConsentState) => ({
		...state,
		choicesOpen: state.status === 'choice-required'
	}));
}
